/** The incident process document, rendered by MarkdownContent. */
export const INCIDENT_PROCESS_MARKDOWN = `## När processen gäller

Gå igenom processen när ett fel hos oss gör att datan i portalen är fel eller gammal för **minst en kund**, och felet inte rättar sig av sig självt vid nästa nattkörning. Det spelar ingen roll om felet drabbar alla kunder eller bara en.

Felet kan ligga i kod, i SIM, i konfiguration eller i infrastrukturen. Exempel:

* En ändring i SIM eller en deploy har gett fel siffror för en eller flera kunder.
* Ett insamlingsjobb har misslyckats, så kundens data är gammal.
* SIM startade innan insamlingen var klar och läste gammal data. Fliken Jobb visar då en ordningsvarning.
* Kunden har ändrat sin koncernstruktur och SIM behöver köras om.
* En störning eller en kvotgräns i AWS gjorde att nattkörningen inte blev av.

Processen gäller **inte** när felet ligger i kundens eget källsystem och kunden själv måste rätta det. Då kontaktar vi kunden som vanligt.

## Roller

| Roll | Vem | Ansvar |
| -- | -- | -- |
| Incidentansvarig | Utveckling | Driver processen från start till slut. Äger rättningen och omkörningen. Ger kundkontakten underlag till meddelandena. |
| Kundkontakt | Team Kund | Skriver och skickar meddelandena i steg 1 och 4 utifrån underlaget. |

Bara utvecklare kan starta och avbryta omkörningar. Alla på Homepal kan följa dem i Datahälsa.

## När kunden ska meddelas

* **Datan är fel:** meddela direkt, innan felet är rättat. Fel siffror är värre än gamla.
* **Datan är gammal:** meddela när den inte rättas av nästa nattkörning, alltså när kunden har gammal data en andra dag i rad. En enstaka missad nattkörning som körs om samma dag kräver inget meddelande.
* **Kunden har hört av sig:** svara alltid, även om felet rättas samma dag.

## Steg

### 1. Meddela kunden om problemet

*Ansvar: kundkontakt, med underlag från incidentansvarig*

1. Ta reda på vilka kunder som påverkas. Översikt i Datahälsa listar kunder som behöver uppmärksamhet, och fliken Jobb visar vilka kunders jobb som har misslyckats.
2. Berätta för teamet i Slack: vad som är fel, vilka kunder som påverkas och vem som är incidentansvarig.
3. Lägg upp en **banner i portalen** via Statusbanner (\`/internal/status-banner\`). Välj nivån *Viktigt* och rikta den bara till de kunder som påverkas.
4. Skicka ett **e-postmeddelande till kundens systemansvarig** enligt mallen nedan. Kontaktuppgifterna finns på kunden.

Meddelandet ska innehålla:

* vad som är fel
* vilka delar av portalen som påverkas
* att vi arbetar med det
* när kunden får nästa besked

### 2. Rätta felet

*Ansvar: incidentansvarig*

1. Skapa ett Linear-issue med etiketten \`bug\`. Länka till Slack-tråden och skriv vilka kunder som påverkas.
2. Hitta grundorsaken, rätta den och deploya.
3. Håller det inte tiden i steg 1, skicka ett nytt besked till kunden med en ny tid.

### 3. Uppdatera datan

*Ansvar: incidentansvarig, från Datahälsa*

1. Öppna Datahälsa och välj **Kör om**. Med en kund vald i kundväljaren körs bara den kunden. Med *Alla* öppnas dialogen med alla kunder förvalda.
2. Välj bort kunder och insamlingsjobb som inte påverkas. Filkunder har inga insamlingsjobb och går direkt till SIM på de filer som finns.
3. Markera de kunder som ska **gå först**. Övriga körs i bokstavsordning, så många åt gången som fönstret tillåter. Prioritera i den här ordningen: kunder som hört av sig, kunder med fel data (inte bara gammal), de största kunderna.
4. Skriv en **beskrivning**: vad som gick fel och länken till Linear-issuet. Den syns i Tidigare körningar.
5. Bekräfta. Varnar dialogen för en krock med nattkörningen eller en kundstartad *Uppdatera data*, vänta hellre tills den körningen är klar.
6. Följ körningen i fliken **Körningar** tills alla kunder är klara. Statusremsan i Datahälsas header visar samma sak på alla flikar.
7. Fallerar ett insamlingsjobb stoppas kundens körning med *Fel i insamling*, och SIM körs inte på gammal data. Rätta orsaken och välj **Kör om fallerade** på omkörningen.
8. **Avbryt** startar inga fler kunder. Kunder som redan kör får köra klart.
9. Kontrollera stickprov i portalen så att datan stämmer för minst en av de kunder som påverkades.

Behöver bara SIM köras om, till exempel efter en ändrad koncernstruktur, räcker det att öppna kundens SIM-jobb i fliken Jobb och välja **Starta jobbet** i detaljpanelen.

### 4. Meddela att felet är åtgärdat

*Ansvar: kundkontakt, samma kanaler som i steg 1*

1. Ta bort bannern, eller byt ut den mot ett meddelande om att felet är åtgärdat.
2. Skicka ett e-postmeddelande till kundens systemansvarig enligt mallen nedan. Det ska säga:
   * vad som hände
   * vad vi har åtgärdat
   * från vilken tidpunkt datan stämmer igen
   * om något fortfarande påverkas
3. Skriv i Slack-tråden att kunderna är meddelade.

## Mallar

Byt ut texten inom klammer. Skriv konkret: vilken flik, vilket mätetal, vilken period.

### Steg 1: problemet

**Ämne:** Störning i Homepal: {vad som är fel}

Hej,

Vi har upptäckt ett fel hos oss som gör att {vilka delar av portalen} i Homepal visar {felaktig / inaktuell} data sedan {tidpunkt}. Ni behöver inte göra något själva.

Vi arbetar med att rätta felet och hör av oss igen senast {dag och tid}.

Hör gärna av er om ni har frågor.

Vänliga hälsningar
{namn}, Homepal

### Steg 4: åtgärdat

**Ämne:** Åtgärdat: {vad som var fel}

Hej,

Felet som gjorde att {vilka delar av portalen} visade {felaktig / inaktuell} data är nu åtgärdat. Orsaken var {kort förklaring}. Datan stämmer igen från och med {tidpunkt}.

{Om något fortfarande påverkas: vad, och när det är löst.}

Tack för ert tålamod.

Vänliga hälsningar
{namn}, Homepal
`
